import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { U21Button, U21ModalBase } from 'app/shared/u21-ui/components';
import { IconX } from '@u21/tabler-icons';
import summerCampImage from 'app/images/summer-camp-campaign.jpeg';
import { LocalStorageKeys } from 'app/shared/constants/localStorage';
import { selectShowSummerCampCampaignEnabled } from 'app/shared/featureFlags/selectors';
import { useLocalStorageState } from 'app/shared/hooks/useLocalStorage';
import { getA11yClickProps } from 'app/shared/utils/a11y';
import styled from 'styled-components';

// Delay before the popup first appears after the app loads.
const POPUP_INITIAL_DELAY_MS = 2 * 60 * 1000; // 2 mins

// Minimum time between showings once the user closes the popup.
const POPUP_RETRIGGER_INTERVAL_MS = 2 * 24 * 60 * 60 * 1000; // 2 days

// Cap on how far ahead we schedule the retrigger within a single session. If
// the next showing is further out than this, we skip scheduling and let the
// next page load re-evaluate — avoids parking a very long-lived timer (and
// stays well under setTimeout's ~24.8 day overflow limit).
const POPUP_MAX_SCHEDULE_AHEAD_MS = 7 * 24 * 60 * 60 * 1000; // 1 week

export const SummerCampCampaignPopup = () => {
  const enabled = useSelector(selectShowSummerCampCampaignEnabled);

  const [lastClosed, setLastClosed] = useLocalStorageState<number>(
    LocalStorageKeys.SUMMER_CAMP_CAMPAIGN_LAST_CLOSED,
    0,
  );

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    // Next showing is the retrigger interval after the last close (or right
    // away if never closed). Clamp to the initial delay so it never pops
    // instantly on load. Reschedules when lastClosed changes so a user who
    // closes and stays on the page still sees it again after the interval.
    const dueAt =
      lastClosed === 0 ? 0 : lastClosed + POPUP_RETRIGGER_INTERVAL_MS;
    const delay = Math.max(POPUP_INITIAL_DELAY_MS, dueAt - Date.now());

    // Skip scheduling if the next showing is more than a week out — the next
    // page load will re-evaluate and schedule it then.
    if (delay > POPUP_MAX_SCHEDULE_AHEAD_MS) {
      return undefined;
    }

    const timer = setTimeout(() => setOpen(true), delay);

    return () => clearTimeout(timer);
  }, [enabled, lastClosed]);

  if (!enabled) {
    return null;
  }

  return (
    <Modal open={open} size="small">
      <Container
        {...getA11yClickProps(() => {
          // Runs on click, not render — getA11yClickProps isn't recognized as
          // an event-handler prop, so the purity rule misreads Date.now().
          // eslint-disable-next-line react-hooks/purity
          setLastClosed(Date.now());
          setOpen(false);
          window.open(
            'https://hubs.li/Q04pz9pT0',
            '_blank',
            'noopener,noreferrer',
          );
        })}
        aria-label="Reserve your spot at Unit21 Summer Camp"
      >
        <CampaignImage
          alt="Unit21 Summer Camp: AI Agent Journey"
          src={summerCampImage}
        />
        <CloseButton
          aria-label="close"
          icon={<IconX />}
          onClick={(e) => {
            e.stopPropagation();
            setLastClosed(Date.now());
            setOpen(false);
          }}
        />
      </Container>
    </Modal>
  );
};

// The campaign artwork is a single fixed-aspect image that must always show in
// full — never scroll. U21ModalBase's small paper reserves 120px top/bottom
// margin and caps height at calc(100% - 240px), which clips the image on short
// viewports. Drop to a minimal 16px margin and let the paper size to the image
// so the whole thing stays visible.
const Modal = styled(U21ModalBase)`
  .MuiDialog-container {
    align-items: center;

    .MuiDialog-paper {
      margin: ${({ theme }) => theme.spacing(2)};
      max-height: calc(100% - ${({ theme }) => theme.spacing(4)});
      /* Hug the image instead of filling the 540px max-width, so a
         height-constrained (narrower) image leaves no blank paper beside it. */
      width: fit-content;
    }
  }
`;

const Container = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.shape.borderRadiusMd}px;
  cursor: pointer;
`;

const CampaignImage = styled.img`
  display: block;
  /* Scale the intrinsic square to fit BOTH the paper width and its usable
     height (100vh minus the 16px top/bottom margin the Modal override sets).
     max-* preserves the aspect ratio against whichever limit binds first, so
     the image never clips or distorts. */
  max-width: 100%;
  max-height: calc(100vh - ${({ theme }) => theme.spacing(4)});
  width: auto;
  height: auto;
`;

const CloseButton = styled(U21Button)`
  position: absolute;
  top: ${({ theme }) => theme.spacing(1)};
  right: ${({ theme }) => theme.spacing(1)};
  background-color: ${({ theme }) => theme.palette.background.neutral};
`;
